import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { apiClient, Notice, User } from '../lib/api'
import { useLiff } from '../hooks/useLiff'

export default function NoticesPage() {
  const { accessToken, isReady } = useLiff()
  const [user, setUser] = useState<User | null>(null)
  const [userLoading, setUserLoading] = useState(true)
  const [notices, setNotices] = useState<Notice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [postForm, setPostForm] = useState({
    title: '',
    body: '',
    is_urgent: false
  })
  const [posting, setPosting] = useState(false)
  const [showForm, setShowForm] = useState(false)

  const loadNotices = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getNotices()
      if (response.success) {
        setNotices(response.notices)
      } else {
        throw new Error(response.error || 'お知らせの取得に失敗しました')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました')
    } finally {
      setLoading(false)
    }
  }

  const handlePost = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!postForm.title.trim() || !postForm.body.trim()) {
      setError('タイトルと本文を入力してください')
      return
    }

    try {
      setPosting(true)
      setError(null)
      const response = await apiClient.createNotice(postForm)
      if (response.success) {
        setPostForm({ title: '', body: '', is_urgent: false })
        setShowForm(false)
        await loadNotices() // 再読み込み
      } else {
        throw new Error(response.error || '投稿に失敗しました')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました')
    } finally {
      setPosting(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('このお知らせを削除してもよろしいですか？')) {
      return
    }

    try {
      const response = await apiClient.deleteNotice(id)
      if (response.success) {
        await loadNotices() // 再読み込み
      } else {
        throw new Error(response.error || '削除に失敗しました')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました')
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleString('ja-JP', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  }

  const loadUser = async () => {
    try {
      setUserLoading(true)
      const response = await apiClient.getMe()
      setUser(response.user)
    } catch (error) {
      console.error('Failed to load user:', error)
    } finally {
      setUserLoading(false)
    }
  }

  useEffect(() => {
    if (isReady && accessToken) {
      apiClient.setAccessToken(accessToken)
      loadUser()
    }
  }, [isReady, accessToken])

  useEffect(() => {
    if (user) {
      loadNotices()
    }
  }, [user])

  if (!isReady || userLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white">
      {/* ヘッダー */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center space-x-3">
            <Link to="/" className="text-yellow-500 text-xl">←</Link>
            <div>
              <h1 className="text-lg font-bold text-gray-900">連絡板</h1>
            </div>
          </div>
          {user?.role === 'admin' && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="bg-yellow-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-yellow-600"
            >
              {showForm ? 'キャンセル' : '投稿'}
            </button>
          )}
        </div>
      </header>

      <main className="p-4 pb-20">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* 投稿フォーム（管理者のみ） */}
        {user?.role === 'admin' && showForm && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">お知らせを投稿</h2>
            <form onSubmit={handlePost} className="space-y-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  タイトル
                </label>
                <input
                  type="text"
                  id="title"
                  value={postForm.title}
                  onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="お知らせのタイトル"
                  maxLength={100}
                  required
                />
              </div>
              
              <div>
                <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-1">
                  本文
                </label>
                <textarea
                  id="body"
                  value={postForm.body}
                  onChange={(e) => setPostForm({ ...postForm, body: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="お知らせの内容"
                  rows={4}
                  maxLength={1000}
                  required
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_urgent"
                  checked={postForm.is_urgent}
                  onChange={(e) => setPostForm({ ...postForm, is_urgent: e.target.checked })}
                  className="h-4 w-4 text-red-500 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="is_urgent" className="ml-2 block text-sm text-gray-900">
                  🚨 緊急通知（Push通知を送信）
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  disabled={posting}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
                  disabled={posting}
                >
                  {posting ? '投稿中...' : '投稿'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* お知らせ一覧 */}
        <div className="space-y-4">
          {notices.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 text-center">
              <div className="text-4xl mb-4">📢</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">お知らせはまだありません</h2>
              <p className="text-gray-600">
                新しいお知らせが投稿されるとここに表示されます
              </p>
            </div>
          ) : (
            notices.map((notice) => (
              <div
                key={notice.id}
                className={`bg-white rounded-xl shadow-lg border p-6 ${
                  notice.is_urgent ? 'border-red-200 bg-red-50' : 'border-gray-100'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {notice.is_urgent && (
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                          🚨 緊急
                        </span>
                      )}
                      <h3 className="text-lg font-bold text-gray-900">{notice.title}</h3>
                    </div>
                    
                    <p className="text-gray-700 mb-3 whitespace-pre-wrap">{notice.body}</p>
                    
                    <div className="flex items-center text-sm text-gray-500 space-x-4">
                      <span>{notice.author_name || '管理者'}</span>
                      <span>{formatDate(notice.created_at)}</span>
                    </div>
                  </div>

                  {user?.role === 'admin' && (
                    <button
                      onClick={() => handleDelete(notice.id)}
                      className="text-red-500 hover:text-red-700 ml-4"
                      title="削除"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  )
}